import * as React from 'react'
import { forwardRef } from 'react'
import type { JSX, MouseEventHandler } from 'react'

import clsx from 'clsx'

import {
  Flex,
  Tag,
  Paragraph,
  VisuallyHidden,
  Heading,
  Checkbox,
  UnstyledButton,
} from '@buffer-mono/popcorn'
import { getUrl } from '@buffer-mono/shared-utils/bibe'

import { MediaCombinedPreview } from '~publish/components/MediaCombinedPreview'
import { MediaTypeMarker } from '~publish/components/MediaMarker'
import type { MediaMarkerType } from '~publish/components/MediaMarker'
import { useHasOrganizationAction } from '~publish/contexts/AccountContext'
import { MediaType } from '~publish/pages/Create/types'
import type { Idea, Media } from '~publish/pages/Create/types'

import {
  getIdeaAccessibleName,
  useSanitizedAndHighlightedText,
} from './helpers'
import { IdeaCard_Idea } from './fragments/IdeaCard'
import type { IdeaCardIdea } from './fragments/IdeaCard'
import { IdeaCardActions } from './IdeaCardActions'

import styles from './IdeaCard.module.css'

const variantClassNames: Partial<Record<'default' | 'compact', string>> = {
  default: styles.default,
}

const CardHeading = ({
  idea,
  titleAs,
}: {
  idea: Idea | IdeaCardIdea
  titleAs: 'h2' | 'h3' | 'h4'
}): JSX.Element | null => {
  return idea.content.title ? (
    <Heading as={titleAs} size="xsmall" className={styles.title} translate="no">
      {idea.content.title}
    </Heading>
  ) : null
}

export { IdeaCard_Idea }
export type { IdeaCardIdea }

export type IdeaCardProps = {
  idea: Idea | IdeaCardIdea
  /** Heading level, so the card fits the hierarchy around it. */
  titleAs?: 'h2' | 'h3' | 'h4'
  variant?: 'default' | 'compact'
  selectable?: boolean
  selected?: boolean
  onSelect?: (id: string) => void
  // TODO: we should just pass id instead of the whole entity
  // for nowm keeping idea object for backward compatibility
  // after usage is refactored, we can remove update it
  onDelete?: (idea: Idea | IdeaCardIdea) => Promise<void>
  onOpen?: (idea: Idea | IdeaCardIdea) => void
  onMoveIntoGroup?: (id: string, groupId: string | null) => void
}

const IdeaCard = forwardRef<HTMLDivElement, IdeaCardProps>(
  (
    {
      variant = 'default',
      idea,
      titleAs = 'h4',
      selectable,
      selected,
      onDelete,
      onOpen,
      onSelect,
      onMoveIntoGroup,
    },
    forwardedRef,
  ) => {
    const canManageIdeas = useHasOrganizationAction('manageIdeas')
    const ideaName = getIdeaAccessibleName(idea)
    const tags = idea?.content?.tags ?? []
    const hasTags = tags.length > 0

    const media = idea?.content?.media ?? []
    const hasMedia = media.length > 0

    const handleClick: MouseEventHandler = (): void => {
      /**
       * If we are in multi-select mode, click on the idea card will select it
       */
      if (selectable) {
        onSelect?.(idea.id)
        return
      }

      onOpen?.(idea)
    }

    const handleDelete = React.useCallback(async (): Promise<void> => {
      await onDelete?.(idea)
    }, [idea, onDelete])

    const handleSelect = (): void => {
      onSelect?.(idea.id)
    }

    const handleMoveIntoGroup = (groupId: string | null): void => {
      onMoveIntoGroup?.(idea.id, groupId)
    }

    return (
      <article
        ref={forwardedRef}
        data-testid="idea-card"
        data-idea-id={idea.id}
        data-idea-position={idea.position}
        data-selected={selected}
        data-selectable={selectable}
        className={clsx(styles.card, variantClassNames[variant])}
      >
        {hasMedia && (
          <div
            className={styles.mediaContainer}
            data-media-count={media.length}
          >
            <MediaCombinedPreview>
              {media.map((mediaItem) => (
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error null is not assignable to type
                <IdeaMedia key={mediaItem.id} media={mediaItem} />
              ))}
            </MediaCombinedPreview>
          </div>
        )}

        <div className={styles.content}>
          <CardHeading idea={idea} titleAs={titleAs} />
          {idea.content.text && <SanitizedIdeaText text={idea.content.text} />}

          {hasTags ? (
            <Flex wrap="wrap" gap="2xs" translate="no">
              {tags.map((tag) => (
                <Tag key={tag.id} color={tag.color}>
                  {tag.name}
                </Tag>
              ))}
            </Flex>
          ) : null}
        </div>

        {/* this button stretches to cover the entire card and add accessible focus and click events */}
        <UnstyledButton
          data-dnd
          className={styles.overlayButton}
          onClick={handleClick}
        >
          <VisuallyHidden>
            {selectable ? `Select idea ${ideaName}` : `Open idea ${ideaName}`}
          </VisuallyHidden>
        </UnstyledButton>

        {selectable && (
          <label
            className={styles.selectionIndicator}
            htmlFor={`select-idea-${idea.id}`}
          >
            <Checkbox
              id={`select-idea-${idea.id}`}
              tabIndex={-1}
              checked={selected}
              onChange={handleSelect}
            />
            <VisuallyHidden>Select idea {ideaName}</VisuallyHidden>
          </label>
        )}

        {!selectable && canManageIdeas && (
          <IdeaCardActions
            idea={idea}
            className={styles.ideaCardMenu}
            onSelect={onSelect ? handleSelect : undefined}
            onDelete={handleDelete}
            onMoveIntoGroup={onMoveIntoGroup ? handleMoveIntoGroup : undefined}
          />
        )}
      </article>
    )
  },
)

IdeaCard.displayName = 'IdeaCard'

type SanitizedIdeaTextProps = {
  text?: string | null
}

const SanitizedIdeaText = ({
  text,
}: SanitizedIdeaTextProps): JSX.Element | null => {
  const htmlText = useSanitizedAndHighlightedText(text)

  if (!text) {
    return null
  }

  return (
    <Paragraph
      className={styles.text}
      translate="no"
      dangerouslySetInnerHTML={{ __html: htmlText }}
    />
  )
}

type IdeaMediaProps = {
  media: Media
}

const ideaTypeToMarker: Partial<Record<MediaType, MediaMarkerType>> = {
  [MediaType.video]: 'video',
  [MediaType.gif]: 'gif',
  [MediaType.document]: 'pdf',
}

const imageMediaTypes: ReadonlySet<MediaType> = new Set([
  MediaType.image,
  MediaType.gif,
])

const getIdeaThumbnailSource = (media: Media): string | null => {
  if (media.thumbnailUrl) {
    return media.thumbnailUrl
  }

  return imageMediaTypes.has(media.type) ? media.url : null
}

const IdeaMedia = ({ media }: IdeaMediaProps): JSX.Element | null => {
  const thumbnailSource = getIdeaThumbnailSource(media)

  // TODO: API should support resizing, this is a temporary solution
  // to alleviate performance concerns
  const optimizedThumbnail = thumbnailSource
    ? getUrl(`https://images.buffer.com/proxy/500,fit/${thumbnailSource}`)
    : null

  const markerType = ideaTypeToMarker[media.type]

  return (
    <div className={styles.mediaWrapper}>
      {optimizedThumbnail && (
        <img
          src={optimizedThumbnail}
          alt={media.alt ?? 'Media attachment'}
          className={styles.media}
          loading="lazy"
        />
      )}
      {markerType && (
        <MediaTypeMarker
          className={styles.mediaTypeIndicator}
          type={markerType}
        />
      )}
    </div>
  )
}

export type IdeaCardSkeletonProps = {
  variant: 1 | 2 | 3 | 4
}

const IdeaCardSkeleton = React.forwardRef<
  React.ElementRef<'div'>,
  IdeaCardSkeletonProps
>(({ variant = 1 }, ref): JSX.Element => {
  return (
    <article
      className={clsx(styles.skeleton, styles[`variant${variant}`])}
      aria-hidden
      ref={ref}
    >
      <div className={styles.media} />
    </article>
  )
})

IdeaCardSkeleton.displayName = 'IdeaCard.Skeleton'

const IdeaCardObject = Object.assign(IdeaCard, {
  Skeleton: IdeaCardSkeleton,
})

export { IdeaCardObject as IdeaCard }
